import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '../../lib/api';
import type {
  EmployeeLocation,
  AssignEmployeesDto,
  UnassignEmployeesDto,
} from '../../types/location.types';

// Получение привязок сотрудников к локации
export const useLocationEmployees = (locationId: string | undefined) => {
  return useQuery({
    queryKey: ['employee-locations', 'location', locationId],
    queryFn: async () => {
      if (!locationId) {
        return [];
      }
      
      try {
        const response = await apiClient.getLocationEmployees(locationId);
        // Убеждаемся, что возвращаем массив
        const data = response?.data || response || [];
        return data as EmployeeLocation[];
      } catch (error) {
        console.error('[useLocationEmployees] API Error:', error);
        // В случае ошибки возвращаем пустой массив вместо undefined
        return [];
      }
    },
    enabled: !!locationId,
  });
};

// Получение привязок локаций к сотруднику
export const useEmployeeLocations = (employeeId: string | undefined) => {
  return useQuery({
    queryKey: ['employee-locations', 'employee', employeeId],
    queryFn: async () => {
      if (!employeeId) {
        console.log('[useEmployeeLocations] No employeeId provided, returning empty array');
        return [];
      }
      
      try {
        const response = await apiClient.getEmployeeLocations(employeeId);
        return (response?.data || []) as EmployeeLocation[];
      } catch (error) {
        console.error('[useEmployeeLocations] API Error:', error);
        return [];
      }
    },
    enabled: !!employeeId,
  });
};

// Привязка сотрудников к локациям
export const useAssignEmployees = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: AssignEmployeesDto) => {
      const response = await apiClient.assignEmployees(data);
      return response;
    },
    onSuccess: () => {
      // Инвалидируем все связанные запросы
      queryClient.invalidateQueries({ queryKey: ['employee-locations'] });
      queryClient.invalidateQueries({ queryKey: ['employees'] });
      queryClient.invalidateQueries({ queryKey: ['locations'] });
      // Принудительно обновляем данные
      queryClient.refetchQueries({ queryKey: ['employee-locations'] });
    },
  });
};

// Отвязка сотрудников от локаций
export const useUnassignEmployees = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: UnassignEmployeesDto) => {
      const response = await apiClient.unassignEmployees(data);
      return response;
    },
    onSuccess: () => {
      // Инвалидируем все связанные запросы
      queryClient.invalidateQueries({ queryKey: ['employee-locations'] });
      queryClient.invalidateQueries({ queryKey: ['employees'] });
      queryClient.invalidateQueries({ queryKey: ['locations'] });
    },
  });
};

// Обновление уровня доступа привязки
export const useUpdateAccessLevel = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      id,
      access_level,
    }: {
      id: string;
      access_level: 'standard' | 'restricted' | 'vip';
    }) => {
      const response = await apiClient.updateAccessLevel(id, { access_level });
      return response;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['employee-locations'] });
    },
  });
};

// Проверка доступа сотрудника к локации
export const useCheckAccess = (employeeId: string, locationId: string) => {
  return useQuery({
    queryKey: ['employee-locations', 'check-access', employeeId, locationId],
    queryFn: async () => {
      const response = await apiClient.checkAccess(employeeId, locationId);
      return response.data as {
        has_access: boolean;
        access_level?: 'standard' | 'restricted' | 'vip';
        valid_from?: string;
        valid_until?: string;
      };
    },
    enabled: !!employeeId && !!locationId,
  });
};